/**
 * Run timeout defaults shared by the server and the template editors (#1957).
 *
 * ## Why these are here and not in the server's domain layer
 *
 * The server owns the authoritative copy in `domain/runTemplates/`, and
 * that is where the schema default and every backend call site read it
 * from. But the three template dialogs in the frontend also pick a value
 * — they always send `playwrightTimeout` on save, so a UI-created
 * template never falls through to the schema default at all. The dialog's
 * number IS the default for anything an operator creates.
 *
 * That made the frontend a fourth source of truth, disagreeing with the
 * other three, and it is precisely the "real duplication or sync pain
 * point" this package's own docstring reserves itself for. The server's
 * `domain/` module re-exports from here rather than holding its own copy,
 * so there is one number, not two that agree today.
 *
 * Runtime constants rather than types is a slight stretch of the package
 * name; the alternative was a new shared package for two numbers, which
 * the "new modules are rare" rule rejects.
 */

/**
 * Test timeout for the main Playwright project, in ms.
 *
 * A test timeout costs nothing while tests pass — it only bounds how long
 * a stuck test waits before giving up. The values this replaced (5s and
 * 10s) bought a faster failure on a hang and paid for it by being unable
 * to complete a federated sign-in at all.
 */
export const DEFAULT_PLAYWRIGHT_TIMEOUT_MS = 30_000

/**
 * Floor for the generated `fixtures` project, in ms. The emitted config
 * uses `max(mainTimeout, this)`, so an operator tuning the main timeout
 * down for fast feedback can never starve sign-in.
 *
 * 90s is the arithmetic of the curated sign-in recipe, not a round
 * number. A clean sign-in is roughly 12s observed. The post-password race
 * allows 30s, the reached-the-app wait another 15s, and a rejected
 * one-time code costs a 5s rejection check plus a 30s wait for a fresh
 * window before retrying. One full retry therefore lands near 80s. Below
 * that, a sign-in that would have recovered gets killed mid-retry.
 */
export const FIXTURE_TIMEOUT_FLOOR_MS = 90_000

/**
 * The sign-in `fixtures` project's real timeout budget, in ms: never below the
 * fixture floor, never below the run's own main timeout (a sign-in must never
 * get less time than an ordinary test). The generated config renders this into
 * the fixtures project, and the orchestrator names the same number when a
 * sign-in helper times out (#1958) — both through this one function, so the
 * limit shown in the run feed can't drift from the limit actually enforced.
 */
export function resolveFixturesTimeoutMs(fixturesFloorMs: number | undefined, mainTimeoutMs: number): number {
  return Math.max(fixturesFloorMs ?? 0, mainTimeoutMs)
}

/**
 * Floor for Playwright's per-ACTION budget, in ms (#2216).
 *
 * The emitted config uses `max(assertionTimeout, this)`, so raising the
 * assertion timeout raises the action budget with it and no existing run
 * drops below the 10s it has today.
 *
 * It needs a floor at all because the two are set independently: an
 * operator who tunes the assertion timeout DOWN for fast feedback would
 * otherwise also shorten every click and fill, which is not what that
 * control says it does.
 *
 * 10s is what the generator has always used, kept deliberately. Whether
 * it is the RIGHT number is a separate question from whether it is
 * reachable, and it was unreachable until now — no caller passed one, so
 * changing it would have taught nothing.
 */
export const ACTION_TIMEOUT_FLOOR_MS = 10_000

/**
 * Values this codebase has shipped as a default `playwrightTimeout`.
 *
 * The boot backfill raises a row ONLY if its value is one of these — a
 * default nobody chose. Any other value is an operator's decision and is
 * left alone, including the 15000 found in the field. "Below the floor"
 * would have been the wrong predicate: it cannot tell a stale default
 * from a deliberate choice.
 */
export const SHIPPED_DEFAULT_TIMEOUTS_MS: readonly number[] = [5_000, 10_000]

/**
 * Starting pause before each browser action in a new run template, in ms
 * (Playwright's `slowMo`). Many sites limit how fast one session can send
 * requests, and a run at full speed trips that limit and fails for reasons
 * unrelated to the app. One second is slow enough for that and still
 * readable when someone watches a run.
 *
 * A starting value only: the variant's auto-created templates and the
 * dialogs' new-template forms use it. Existing templates keep whatever
 * they store, and the schema has no default on purpose, because Mongoose
 * fills a schema default into older rows that lack the field when it
 * loads them.
 */
export const DEFAULT_SLOW_MOTION_MS = 1_000

/**
 * Starting pause after each test in a new run template, in ms. The
 * between-tests companion to `DEFAULT_SLOW_MOTION_MS`, with the same
 * reason and the same new-templates-only scope.
 */
export const DEFAULT_INTER_TEST_DELAY_MS = 2_000
